import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './primitives';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
  /** "sheet": masaüstünde sağdan, mobilde alttan açılan panel. */
  variant?: 'dialog' | 'sheet';
  className?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closeLabel,
  variant = 'dialog',
  className,
}: DialogProps) {
  const sheet = variant === 'sheet';
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ink/35 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 flex max-h-[92dvh] flex-col bg-surface text-ink shadow-pop focus:outline-none',
            sheet
              ? 'inset-x-0 bottom-0 rounded-t-md sm:inset-y-0 sm:start-auto sm:end-0 sm:max-h-none sm:w-[min(36rem,100vw)] sm:rounded-none sm:border-s sm:border-line'
              : 'inset-x-3 top-[8dvh] mx-auto max-w-lg rounded-md border border-line',
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <DialogPrimitive.Title className="font-cond text-lg font-semibold">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-sm text-ink-2">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="rounded-sm p-1.5 text-ink-3 hover:bg-surface-2 hover:text-ink"
              aria-label={closeLabel}
            >
              <X className="h-5 w-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Popover({
  trigger,
  children,
  label,
  open,
  onOpenChange,
}: {
  trigger: ReactNode;
  children: ReactNode;
  label: string;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          aria-label={label}
          sideOffset={6}
          collisionPadding={12}
          className="z-50 w-[min(24rem,calc(100vw-24px))] rounded-md border border-line bg-surface p-0 text-ink shadow-pop focus:outline-none"
        >
          {children}
          <PopoverPrimitive.Arrow className="fill-surface" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export interface InsertContent {
  title: string;
  short: string;
  whatItDoes?: string | null;
  whenToUse?: string | null;
  dataChanged?: string | null;
  requiredPermission?: string | null;
  reversible?: string | null;
  commonErrors?: Array<{ problem: string; solution: string }> | null;
  guideUrl?: string | null;
}

export interface InsertLabels {
  whatItDoes: string;
  whenToUse: string;
  dataChanged: string;
  requiredPermission: string;
  reversible: string;
  commonErrors: string;
  guide: string;
}

/**
 * "Prospektüs": yardım içeriğinin görsel dili. Takviye kutusundan çıkan kullanım talimatı gibi,
 * kısa ve bölümlenmiş. Sayfa açıklamasında ve buton yardımlarında aynı biçim kullanılır.
 */
export function InsertBody({
  content,
  labels,
  compact,
}: {
  content: InsertContent;
  labels: InsertLabels;
  compact?: boolean;
}) {
  const rows: Array<[string, string | null | undefined]> = [
    [labels.whatItDoes, content.whatItDoes],
    [labels.whenToUse, content.whenToUse],
    [labels.dataChanged, content.dataChanged],
    [labels.requiredPermission, content.requiredPermission],
    [labels.reversible, content.reversible],
  ];
  return (
    <div className={cn('text-[0.875rem] leading-relaxed text-ink', compact ? 'space-y-2.5' : 'space-y-3')}>
      <p className="text-ink">{content.short}</p>
      {rows.some(([, v]) => v) && (
        <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[9.5rem_1fr]">
          {rows
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-cond text-[0.8125rem] font-semibold text-pine">{k}</dt>
                <dd className="text-ink-2">{v}</dd>
              </div>
            ))}
        </dl>
      )}
      {content.commonErrors && content.commonErrors.length > 0 && (
        <div>
          <p className="font-cond text-[0.8125rem] font-semibold text-pine">{labels.commonErrors}</p>
          <ul className="mt-1 space-y-1.5">
            {content.commonErrors.map((e) => (
              <li key={e.problem} className="text-ink-2">
                <span className="font-medium text-ink">{e.problem}</span> — {e.solution}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Yalnızca açılabilir bir adres gösterilir; depo içi belge yolları kullanıcıya bir şey ifade etmez. */}
      {content.guideUrl && /^https?:\/\//.test(content.guideUrl) && (
        <p className="text-[0.8125rem]">
          <a
            href={content.guideUrl}
            target="_blank"
            rel="noreferrer"
            className="text-pine underline underline-offset-2"
          >
            {labels.guide}
          </a>
        </p>
      )}
    </div>
  );
}

/** Sayfanın üst kısmındaki "Bu sayfa ne işe yarar?" prospektüsü (açılır-kapanır). */
export function PageInsert({
  heading,
  content,
  labels,
  defaultOpen,
}: {
  heading: string;
  content: InsertContent;
  labels: InsertLabels;
  defaultOpen: boolean;
}) {
  return (
    <details
      className="group relative mb-6 overflow-hidden rounded-md border border-line bg-insert ps-4 [&_summary::-webkit-details-marker]:hidden"
      open={defaultOpen}
    >
      <span className="absolute inset-y-0 start-0 w-[3px] bg-insert-rule" aria-hidden />
      <summary className="flex cursor-pointer list-none items-center gap-2 py-3 pe-8 font-cond text-[0.9375rem] font-semibold text-ink">
        <span
          className="inline-block transition-transform group-open:rotate-90 rtl:group-open:-rotate-90"
          aria-hidden
        >
          ›
        </span>
        {heading}
      </summary>
      <div className="max-w-[78ch] pb-4 pe-5">
        <InsertBody content={content} labels={labels} />
      </div>
    </details>
  );
}
